import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CSelect,
  CBadge,
  CTooltip,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  createItem,
  updateItem,
  ItemRequestStatus,
  getList,
} from "../../api/generics";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert } from "../utils/messages";
import { emptyValueOnUndefined } from "../../utils/fields";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import Category from "../../models/category";
import Discount, {
  newDiscount,
  getDiscountTypeLabel,
  DISCOUNT_TYPE_HELP,
  DISCOUNT_TYPES_ORDER,
} from "../../models/discount";
import Store from "../../models/store";
import CategoriesSelect from "../category/CategoriesSelect";
import StoresSelect from "../store/StoresSelect";
import AnimatedCheckbox from "../checkbox/AnimatedCheckbox";
import Product from "../../models/product";
import ProductsSelect from "../product/productsSelect";

interface DiscountFormProps {
  initialDiscount?: Discount;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const DiscountForm: React.FC<DiscountFormProps> = ({
  initialDiscount,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const company = useSelector((state: RootState) => state.company.data.company);
  const [editingDiscount, setEditingDiscount] = useState<Discount>(
    initialDiscount ? initialDiscount : newDiscount(),
  );

  const [categories, setCategories] = useState<readonly Category[]>([]);
  const [products, setProducts] = useState<readonly Product[]>([]);
  const [stores, setStores] = useState<readonly Store[]>([]);
  const [loading, setLoading] = useState(true);

  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {},
  );
  const [submitting, setSubmitting] = useState(false);
  const [showSpecialFiels, setShowSpecialFields] = useState(false);

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingDiscount({
      ...editingDiscount,
      name: e.target.value,
    });
  };

  const onDiscountTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === "two_levels") {
      setShowSpecialFields(true);
    } else {
      setShowSpecialFields(false);
    }
    setEditingDiscount((editingDiscount) => {
      const newDiscountType =
        e.target.value !== "" ? e.target.value : undefined;
      return {
        ...editingDiscount,
        discountType: newDiscountType,
        scopeType:
          newDiscountType === "two_levels"
            ? "category"
            : editingDiscount.scopeType,
      };
    });
  };

  const onDiscountScopeTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setEditingDiscount((editingDiscount) => {
      const newDiscountScopeType =
        e.target.value !== "" ? e.target.value : undefined;
      return {
        ...editingDiscount,
        scopeType: newDiscountScopeType,
      };
    });
  };

  const onApplyToRetailChange = (newValue: boolean) => {
    setEditingDiscount({
      ...editingDiscount,
      applyToRetail: newValue,
    });
  };

  const onApplyToWholesaleChange = (newValue: boolean) => {
    setEditingDiscount({
      ...editingDiscount,
      applyToWholesale: newValue,
    });
  };

  const onApplyToSuperholesaleChange = (newValue: boolean) => {
    setEditingDiscount({
      ...editingDiscount,
      applyToSuperwholesale: newValue,
    });
  };

  const onPercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAmountNumber = Number(e.target.value);
    setEditingDiscount({
      ...editingDiscount,
      discountPercentage: newAmountNumber,
    });
  };

  const onFirstThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAmountNumber = Number(e.target.value);
    setEditingDiscount({
      ...editingDiscount,
      firstThreshold: newAmountNumber,
    });
  };

  const onSecondThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAmountNumber = Number(e.target.value);
    setEditingDiscount({
      ...editingDiscount,
      secondThreshold: newAmountNumber,
    });
  };

  const onSecondAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAmountNumber = Number(e.target.value);
    setEditingDiscount({
      ...editingDiscount,
      secondDiscountValue: newAmountNumber,
    });
  };

  const onSave = async () => {
    setSubmitting(true);
    const newErrors: Errors = {};

    // Validar scopeType
    if (!editingDiscount.scopeType || editingDiscount.scopeType === "") {
      newErrors.scopeType = [
        "Debe seleccionar una opción (Categoría o Producto)",
      ];
    }

    if (!editingDiscount.discountType || editingDiscount.discountType === "") {
      newErrors.discountType = ["Debe seleccionar un Tipo de descuento"];
    }

    const mutableCategories: Category[] = Array.from(categories);
    const mutableProducts: Product[] = Array.from(products);
    const mutableStores: Store[] = Array.from(stores);

    // Validar categorías o productos según scopeType
    if (
      editingDiscount.scopeType === "category" &&
      mutableCategories.length === 0
    ) {
      newErrors.categories = ["Debe seleccionar por lo menos una categoría"];
    } else if (
      editingDiscount.scopeType === "product" &&
      mutableProducts.length === 0
    ) {
      newErrors.products = ["Debe seleccionar por lo menos un producto"];
    }

    // Validar sucursales
    if (mutableStores.length === 0) {
      newErrors.stores = ["Debe seleccionar por lo menos una sucursal"];
    }

    // Validar tipo de precio
    if (
      !editingDiscount.applyToRetail &&
      !editingDiscount.applyToWholesale &&
      !editingDiscount.applyToSuperwholesale
    ) {
      newErrors.applyToRetail = [
        "Debe seleccionar al menos un tipo de precio (Minorista, Mayorista o Super Mayorista)",
      ];
    }

    if (
      (editingDiscount.discountType === "cashback" ||
        editingDiscount.discountType === "cashback_only_ticket") &&
      !(editingDiscount.discountPercentage! > 0)
    ) {
      newErrors.discountPercentage = [
        "El monto a reintegrar debe ser mayor a 0",
      ];
    }

    // Si hay errores, los mostramos y salimos
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSubmitting(false);
      return;
    }

    let toSendDiscount: Discount = {
      ...editingDiscount,
      companyId: company.id,
      stores: mutableStores,
    };

    if (editingDiscount.scopeType === "category") {
      toSendDiscount.categories = mutableCategories;
    }

    if (editingDiscount.scopeType === "product") {
      toSendDiscount.products = mutableProducts;
    }

    if (editingDiscount.discountType === "two_levels") {
      toSendDiscount.applyToRetail = true;
      toSendDiscount.applyToWholesale = true;
    }

    if (toSendDiscount.firstThreshold === undefined) {
      toSendDiscount.firstThreshold = null;
    }
    if (toSendDiscount.secondThreshold === undefined) {
      toSendDiscount.secondThreshold = null;
    }
    if (toSendDiscount.secondDiscountValue === undefined) {
      toSendDiscount.secondDiscountValue = null;
    }
    let requestPromise: Promise<ItemRequestStatus<Discount>>;
    if (editingDiscount.id === undefined) {
      toSendDiscount.isActive = true;
      requestPromise = createItem<Discount>(
        "/discounts/create/",
        toSendDiscount,
      );
    } else {
      requestPromise = updateItem<Discount>(
        `/discounts/${toSendDiscount.id}/`,
        toSendDiscount,
      );
    }

    const discountStatus = await requestPromise;

    if (discountStatus.status !== SUCCESS) {
      if (discountStatus.errors !== undefined) {
        setErrors(discountStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (discountStatus.detail !== undefined) {
        message = discountStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      setLoading(false);
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingDiscount(newDiscount());
    setCategories([]);
    setProducts([]);
    setStores([]);
    setShowSpecialFields(false);
  };

  const fetchCategories = useCallback(async () => {
    if (editingDiscount === undefined || editingDiscount.id === undefined) {
      setCategories([]);
      return [];
    }
    const discountIdStr = editingDiscount.id.toString();
    if (discountIdStr === undefined) {
      setCategories([]);
      return [];
    }
    const limit = 100;
    const offset = 0;
    const additional = new Map();
    additional.set("discount_id", discountIdStr);
    additional.set("company_id", company.id!.toString());
    const categoriesStatus = await getList<Category>(
      "/categories/",
      limit,
      offset,
      additional,
    );

    if (categoriesStatus.status === SUCCESS) {
      if (categoriesStatus.data !== undefined) {
        setCategories(categoriesStatus.data.items);
      }
    } else {
      setCategories([]);
    }
  }, [editingDiscount]);

  const fetchProducts = useCallback(async () => {
    if (editingDiscount === undefined || editingDiscount.id === undefined) {
      setProducts([]);
      return [];
    }
    const discountIdStr = editingDiscount.id.toString();
    if (discountIdStr === undefined) {
      setProducts([]);
      return [];
    }
    const limit = 100;
    const offset = 0;
    const additional = new Map();
    additional.set("discount_id", discountIdStr);
    additional.set("company_id", company.id!.toString());
    const productsStatus = await getList<Product>(
      "/clothing_products/",
      limit,
      offset,
      additional,
    );

    if (productsStatus.status === SUCCESS) {
      if (productsStatus.data !== undefined) {
        setProducts(productsStatus.data.items);
      }
    } else {
      setProducts([]);
    }
  }, [editingDiscount]);

  const fetchStores = useCallback(async () => {
    if (editingDiscount === undefined || editingDiscount.id === undefined) {
      setStores([]);
      return [];
    }
    const discountIdStr = editingDiscount.id.toString();
    if (discountIdStr === undefined) {
      setStores([]);
      return [];
    }
    const limit = 100;
    const offset = 0;
    const additional = new Map();
    additional.set("discount_id", discountIdStr);
    additional.set("company_id", company.id!.toString());
    const BranchOfficesStatus = await getList<Store>(
      "/stores/",
      limit,
      offset,
      additional,
    );

    if (BranchOfficesStatus.status === SUCCESS) {
      if (BranchOfficesStatus.data !== undefined) {
        setStores(BranchOfficesStatus.data.items);
      }
    } else {
      setStores([]);
    }
  }, [editingDiscount]);

  const getInitialValues = useCallback(async () => {
    setLoading(true);
    const productsPromise = fetchProducts();
    const categoriesPromise = fetchCategories();
    const storesPromise = fetchStores();
    await categoriesPromise;
    await productsPromise;
    await storesPromise;
    setLoading(false);
  }, [editingDiscount]);

  useEffect(() => {
    setEditingDiscount(initialDiscount ? initialDiscount : newDiscount());
    if (initialDiscount?.discountType === "two_levels") {
      setShowSpecialFields(true);
    } else {
      setShowSpecialFields(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDiscount]);

  useEffect(() => {
    getInitialValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingDiscount.id]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);
  const buttonText1 = loading ? "Cargando..." : "Guardar";
  const buttonText = submitting ? "Guardando..." : buttonText1;

  return (
    <>
      <fieldset disabled={submitting}>
        <CRow>
          <CCol>
            <p className={"mb-1"}>
              <b>Tipos de descuento:</b> pase el mouse por encima de cada uno
              para ver cómo funciona.
            </p>
            <p>
              {DISCOUNT_TYPES_ORDER.map((discountTypeKey) => (
                <CTooltip
                  key={discountTypeKey}
                  content={DISCOUNT_TYPE_HELP[discountTypeKey]}
                >
                  <CBadge
                    shape="pill"
                    color={
                      editingDiscount.discountType === discountTypeKey
                        ? "primary"
                        : "secondary"
                    }
                    className={"mr-1 p-2"}
                    style={{ cursor: "help", fontSize: "0.9em" }}
                  >
                    {getDiscountTypeLabel(discountTypeKey)}
                  </CBadge>
                </CTooltip>
              ))}
            </p>
            {/* La explicación del tipo elegido también se muestra sin hover:
                es la que el usuario necesita mientras completa el formulario. */}
            {editingDiscount.discountType &&
            DISCOUNT_TYPE_HELP[editingDiscount.discountType] ? (
              <p className={"text-muted"}>
                <b>{getDiscountTypeLabel(editingDiscount.discountType)}:</b>{" "}
                {DISCOUNT_TYPE_HELP[editingDiscount.discountType]}
              </p>
            ) : (
              <></>
            )}
          </CCol>
        </CRow>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Nombre:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingDiscount.name)}
                onChange={onNameChange}
                placeholder="Nombre"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("name", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Tipo de Descuento:</CLabel>
            </CCol>
            <CCol>
              <CSelect
                type="text"
                onChange={onDiscountTypeChange}
                value={
                  editingDiscount.discountType
                    ? editingDiscount.discountType
                    : ""
                }
              >
                <option value={""}>-----</option>
                <option value={"percentage"}>Porcentaje</option>
                <option value={"fixed_amount"}>Descuento de Monto Fijo</option>
                <option value={"set_price"}>Fijar Precio </option>
                <option value={"two_levels"}>Dos Niveles </option>
                <option value={"cashback"}>Reintegro</option>
                <option value={"cashback_only_ticket"}>
                  Reintegro solo ticket
                </option>
              </CSelect>
              <FieldErrors
                errors={getFieldErrors("discountType", errors) as string[]}
              ></FieldErrors>
            </CCol>
            {editingDiscount.discountType !== "two_levels" && (
              <>
                <CCol md={2}>
                  <CLabel>Descuento de:</CLabel>
                </CCol>
                <CCol>
                  <CSelect
                    type="text"
                    onChange={onDiscountScopeTypeChange}
                    value={
                      editingDiscount.scopeType ? editingDiscount.scopeType : ""
                    }
                  >
                    <option value={""}>-----</option>
                    <option value={"category"}>Categorías</option>
                    <option value={"product"}>Productos</option>
                  </CSelect>
                  <FieldErrors
                    errors={getFieldErrors("scopeType", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </>
            )}
          </CRow>
        </CFormGroup>
        {editingDiscount.discountType !== "two_levels" && (
          <CFormGroup>
            <CRow>
              <CCol md={2}>
                <CLabel>Aplicar a Precio lista?:</CLabel>
              </CCol>
              <CCol>
                <CRow>
                  <AnimatedCheckbox
                    value={editingDiscount.applyToRetail}
                    onChange={onApplyToRetailChange}
                  ></AnimatedCheckbox>
                  <FieldErrors
                    errors={getFieldErrors("applyToRetail", errors) as string[]}
                  ></FieldErrors>
                </CRow>
              </CCol>
              <CCol md={2}>
                <CLabel>Aplicar a Precio Mayorista?:</CLabel>
              </CCol>
              <CCol>
                <CRow>
                  <AnimatedCheckbox
                    value={editingDiscount.applyToWholesale}
                    onChange={onApplyToWholesaleChange}
                  ></AnimatedCheckbox>
                  <FieldErrors
                    errors={
                      getFieldErrors("applyToWholesale", errors) as string[]
                    }
                  ></FieldErrors>
                </CRow>
              </CCol>
              <CCol md={2}>
                <CLabel>Aplicar a Precio Supermayorista?:</CLabel>
              </CCol>
              <CCol>
                <CRow>
                  <AnimatedCheckbox
                    value={editingDiscount.applyToSuperwholesale}
                    onChange={onApplyToSuperholesaleChange}
                  ></AnimatedCheckbox>
                  <FieldErrors
                    errors={
                      getFieldErrors(
                        "applyToSuperwholesale",
                        errors,
                      ) as string[]
                    }
                  ></FieldErrors>
                </CRow>
              </CCol>
            </CRow>
          </CFormGroup>
        )}
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>
                {showSpecialFiels
                  ? "Primer descuento"
                  : editingDiscount.discountType === "cashback"
                    ? "Monto a devolver por unidad:"
                    : editingDiscount.discountType === "cashback_only_ticket"
                      ? "Monto a reintegrar por unidad:"
                      : "Monto del descuento:"}
              </CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(
                  editingDiscount.discountPercentage,
                )}
                onChange={onPercentageChange}
                placeholder={
                  editingDiscount.discountType === "cashback"
                    ? "Monto a devolver"
                    : editingDiscount.discountType === "cashback_only_ticket"
                      ? "Monto a reintegrar"
                      : "Monto de descuento"
                }
              ></CInput>
              <FieldErrors
                errors={
                  getFieldErrors("discountPercentage", errors) as string[]
                }
              ></FieldErrors>
            </CCol>
            {showSpecialFiels ? (
              <>
                <CCol md={2}>
                  <CLabel>Valor a alcanzar 1:</CLabel>
                </CCol>
                <CCol>
                  <CInput
                    type="text"
                    value={emptyValueOnUndefined(
                      editingDiscount.firstThreshold === null
                        ? undefined
                        : editingDiscount.firstThreshold,
                    )}
                    onChange={onFirstThresholdChange}
                    placeholder="Valor a alcanzar 1"
                  ></CInput>
                  <FieldErrors
                    errors={
                      getFieldErrors("firstThreshold", errors) as string[]
                    }
                  ></FieldErrors>
                </CCol>
              </>
            ) : (
              <></>
            )}
          </CRow>
        </CFormGroup>
        {showSpecialFiels ? (
          <CFormGroup>
            <CRow>
              <CCol md={2}>
                <CLabel>Segundo Descuento:</CLabel>
              </CCol>
              <CCol>
                <CInput
                  type="text"
                  value={emptyValueOnUndefined(
                    editingDiscount.secondDiscountValue === null
                      ? undefined
                      : editingDiscount.secondDiscountValue,
                  )}
                  onChange={onSecondAmountChange}
                  placeholder="Monto de descuento"
                ></CInput>
                <FieldErrors
                  errors={
                    getFieldErrors("secondDiscountValue", errors) as string[]
                  }
                ></FieldErrors>
              </CCol>
              <CCol md={2}>
                <CLabel>Valor a alcanzar 2:</CLabel>
              </CCol>
              <CCol>
                <CInput
                  type="text"
                  value={emptyValueOnUndefined(
                    editingDiscount.secondThreshold === null
                      ? undefined
                      : editingDiscount.secondThreshold,
                  )}
                  onChange={onSecondThresholdChange}
                  placeholder="Valor a alcanzar 2"
                ></CInput>
                <FieldErrors
                  errors={getFieldErrors("secondThreshold", errors) as string[]}
                ></FieldErrors>
              </CCol>
            </CRow>
          </CFormGroup>
        ) : (
          <></>
        )}
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Tiendas:</CLabel>
            </CCol>
            <CCol>
              <StoresSelect
                value={stores}
                onChange={setStores}
                isDisabled={loading || submitting}
              ></StoresSelect>
              <FieldErrors
                errors={getFieldErrors("stores", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        {editingDiscount.scopeType === "product" ? (
          <CFormGroup>
            <CRow>
              <CCol md={2}>
                <CLabel>Productos:</CLabel>
              </CCol>
              <CCol>
                <ProductsSelect
                  value={products}
                  onChange={setProducts}
                  isDisabled={loading || submitting}
                ></ProductsSelect>
                <FieldErrors
                  errors={getFieldErrors("products", errors) as string[]}
                ></FieldErrors>
              </CCol>
            </CRow>
          </CFormGroup>
        ) : (
          <CFormGroup>
            <CRow>
              <CCol md={2}>
                <CLabel>Categorías:</CLabel>
              </CCol>
              <CCol>
                <CategoriesSelect
                  value={categories}
                  onChange={setCategories}
                  isDisabled={loading || submitting}
                ></CategoriesSelect>
                <FieldErrors
                  errors={getFieldErrors("categories", errors) as string[]}
                ></FieldErrors>
              </CCol>
            </CRow>
          </CFormGroup>
        )}
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting || loading ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {buttonText}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default DiscountForm;
